/**
 * Builds the Schedule Task menu from its state and Employee access.
 * Offers Poke Agent only with effective delegation permission and enabled AI features.
 * ScheduleTaskPanelContent.tsx runs the selected action.
 */
import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DIVIDER_ID } from '@modernloop/shared/constants';
import { TouchAppIcon } from '@modernloop/shared/icons';

import { TaskStatus, UseSchduleTaskMenuOptions_TaskFragment } from 'src/generated/mloop-graphql';

import { FunctionWithFragments } from 'src/components/types';

import useCanDelegateToSchedulingAgent from 'src/hooks/useCanDelegateToSchedulingAgent';

import { ScheduleTaskMenuItemProps } from 'src/views-new/ScheduleTask/ScheduleTaskDetails';

export enum ScheduleTaskMenuOptions {
  MarkAsUrgent = 'MarkAsUrgent',
  RemoveUrgent = 'RemoveUrgent',
  CopyLinkToTask = 'CopyLinkToTask',
  CloseTask = 'CloseTask',
  PokeAgent = 'PokeAgent',
}

const AGENT_DELEGATE_TYPE = 'AGENT';

type UseScheduleTaskMenuOptionsFragmentProps = {
  task: UseSchduleTaskMenuOptions_TaskFragment | undefined;
};

/** Preserves regular menu actions when Agent write permission is absent. */
const useSchduleTaskMenuOptions: FunctionWithFragments<
  UseScheduleTaskMenuOptionsFragmentProps,
  undefined,
  ScheduleTaskMenuItemProps[] | undefined
> = ({ task }) => {
  const { t } = useTranslation();
  const canDelegateToSchedulingAgent = useCanDelegateToSchedulingAgent();

  return useMemo(() => {
    if (!task) {
      return undefined;
    }

    const options: ScheduleTaskMenuItemProps[] = [];

    options.push({
      id: ScheduleTaskMenuOptions.CopyLinkToTask,
      value: t('hiring-dashboard-tasks:tasks.scheduleTaskMenuOptions.copyLinkToTask'),
    });

    if (canDelegateToSchedulingAgent && task.delegateType === AGENT_DELEGATE_TYPE) {
      options.push({
        id: ScheduleTaskMenuOptions.PokeAgent,
        value: t('hiring-dashboard-tasks:tasks.scheduleTaskMenuOptions.pokeAgent'),
        icon: <TouchAppIcon fontSize="small" />,
      });
    }

    if (task.status !== TaskStatus.Canceled) {
      options.push({ id: DIVIDER_ID });
      options.push({
        id: ScheduleTaskMenuOptions.CloseTask,
        color: 'error',
        value: t('hiring-dashboard-tasks:tasks.cancelTask.confirmLabel'),
      });
    }

    return options;
  }, [canDelegateToSchedulingAgent, task, t]);
};

useSchduleTaskMenuOptions.fragments = {
  task: gql`
    fragment UseSchduleTaskMenuOptions_task on Task {
      id
      isUrgent
      status
      delegateType
    }
  `,
};

export default useSchduleTaskMenuOptions;
